import React, { useState } from 'react';
import { FormDrawer } from './FormDrawer';
import {
  formatPhoneForCountry,
  normalizeProperCaseText,
  PHONE_MAX_LENGTH,
  phoneErrorForCountry,
  phonePlaceholderForCountry,
} from '../../utils/textUtils';
import {
  getActiveChannelOptions,
  getActiveIndustryOptions,
  getActiveSubindustryOptions,
} from '../placeholders/commercialCatalogDefaults';
import { useBusinessUnit } from '../../contexts/BusinessUnitContext';
import {
  canonicalCountry,
  getCountryGeography,
  localitiesForRegion,
  phoneMetaForCountry,
} from '../../config/countryGeography';

/** Canal de organización: uno o varios valores separados por `;`. */
export const parseOrganizationChannels = (value) =>
  String(value || '')
    .split(/[;,|]/)
    .map((part) => part.trim())
    .filter(Boolean);

export const serializeOrganizationChannels = (values) => {
  const seen = new Set();
  const ordered = [];
  (Array.isArray(values) ? values : parseOrganizationChannels(values)).forEach((raw) => {
    const next = String(raw || '').trim();
    if (!next || seen.has(next)) return;
    seen.add(next);
    ordered.push(next);
  });
  return ordered.join('; ');
};

/** Segmento (A001_organizations.segment): catálogo cerrado */
export const ORGANIZATION_COUNTRY_OPTIONS = [
  { value: 'Chile', label: 'Chile' },
  { value: 'Perú', label: 'Perú' },
  { value: 'Argentina', label: 'Argentina' },
  { value: 'Bolivia', label: 'Bolivia' },
  { value: 'Brasil', label: 'Brasil' },
  { value: 'Colombia', label: 'Colombia' },
  { value: 'Ecuador', label: 'Ecuador' },
  { value: 'México', label: 'México' },
  { value: 'Paraguay', label: 'Paraguay' },
  { value: 'Uruguay', label: 'Uruguay' },
  { value: 'Estados Unidos', label: 'Estados Unidos' },
  { value: 'España', label: 'España' },
  { value: 'Otro', label: 'Otro' },
];

export const ORGANIZATION_SEGMENT_OPTIONS = [
  { value: 'B2B', label: 'B2B' },
  { value: 'B2C', label: 'B2C' },
  { value: 'B2G', label: 'B2G' },
  { value: 'B2B2C', label: 'B2B2C' },
  { value: 'Sin Asignar', label: 'Sin Asignar' },
];

export const ORGANIZATION_INDUSTRY_OPTIONS = [
  { value: 'Administración Pública', label: 'Administración Pública' },
  { value: 'FFAA', label: 'FFAA' },
  { value: 'Educación', label: 'Educación' },
  { value: 'Salud', label: 'Salud' },
  { value: 'Minería', label: 'Minería' },
  { value: 'Logística', label: 'Logística' },
  { value: 'Energía', label: 'Energía' },
  { value: 'Construcción Naval', label: 'Construcción Naval' },
  { value: 'Alimentos', label: 'Alimentos' },
  { value: 'Construcción', label: 'Construcción' },
  { value: 'Turismo', label: 'Turismo' },
  { value: 'Montaje y Mantención', label: 'Montaje y Mantención' },
  { value: 'OEM', label: 'OEM' },
  { value: 'Modulares', label: 'Modulares' },
];

export const ORGANIZATION_SUBINDUSTRY_BY_INDUSTRY = {
  'Administración Pública': [
    { value: 'Municipalidades', label: 'Municipalidades' },
    { value: 'Servicios Públicos', label: 'Servicios Públicos' },
    { value: 'Gobierno Central', label: 'Gobierno Central' },
  ],
  FFAA: [
    { value: 'Ejército', label: 'Ejército' },
    { value: 'Armada', label: 'Armada' },
    { value: 'Fuerza Aérea', label: 'Fuerza Aérea' },
  ],
  Educación: [
    { value: 'Universidades', label: 'Universidades' },
    { value: 'Colegios', label: 'Colegios' },
    { value: 'Institutos Técnicos', label: 'Institutos Técnicos' },
  ],
  Salud: [
    { value: 'Hospitales', label: 'Hospitales' },
    { value: 'Clínicas', label: 'Clínicas' },
    { value: 'Laboratorios', label: 'Laboratorios' },
  ],
  Minería: [
    { value: 'Gran Minería', label: 'Gran Minería' },
    { value: 'Mediana Minería', label: 'Mediana Minería' },
    { value: 'Servicios Mineros', label: 'Servicios Mineros' },
  ],
  Logística: [
    { value: 'Transporte Terrestre', label: 'Transporte Terrestre' },
    { value: 'Centros de Distribución', label: 'Centros de Distribución' },
    { value: 'Operadores Logísticos', label: 'Operadores Logísticos' },
  ],
  Energía: [
    { value: 'Generación', label: 'Generación' },
    { value: 'Transmisión', label: 'Transmisión' },
    { value: 'Distribución', label: 'Distribución' },
  ],
  'Construcción Naval': [
    { value: 'Astilleros', label: 'Astilleros' },
    { value: 'Reparación Naval', label: 'Reparación Naval' },
    { value: 'Proveedores Marítimos', label: 'Proveedores Marítimos' },
  ],
  Alimentos: [
    { value: 'Procesamiento', label: 'Procesamiento' },
    { value: 'Bebidas', label: 'Bebidas' },
    { value: 'Cadena Frío', label: 'Cadena Frío' },
  ],
  Construcción: [
    { value: 'Inmobiliaria', label: 'Inmobiliaria' },
    { value: 'Obras Civiles', label: 'Obras Civiles' },
    { value: 'Terminaciones', label: 'Terminaciones' },
  ],
  Turismo: [
    { value: 'Hotelería', label: 'Hotelería' },
    { value: 'Gastronomía', label: 'Gastronomía' },
    { value: 'Operadores Turísticos', label: 'Operadores Turísticos' },
  ],
  'Montaje y Mantención': [
    { value: 'Mantenimiento Industrial', label: 'Mantenimiento Industrial' },
    { value: 'Montaje Electromecánico', label: 'Montaje Electromecánico' },
    { value: 'Paradas de Planta', label: 'Paradas de Planta' },
  ],
  OEM: [
    { value: 'Fabricantes de Equipos', label: 'Fabricantes de Equipos' },
    { value: 'Integradores', label: 'Integradores' },
    { value: 'Postventa OEM', label: 'Postventa OEM' },
  ],
  Modulares: [
    { value: 'Habitacionales', label: 'Habitacionales' },
    { value: 'Industriales', label: 'Industriales' },
    { value: 'Comerciales', label: 'Comerciales' },
  ],
};

const ORGANIZATION_CLASSIFICATION_OPTIONS = ORGANIZATION_SEGMENT_OPTIONS;

export const OrganizationsForm = ({
  isOpen,
  onClose,
  onSave,
  isSaving = false,
  isDarkMode = false,
  isEditing = false,
  formData,
  onChange,
  formError = '',
  executiveOptions = [],
  existingOrganizations = [],
  currentOrganizationId = null,
}) => {
  const businessUnit = useBusinessUnit();
  const [phoneError, setPhoneError] = useState('');
  const country = canonicalCountry(formData?.country) || 'Chile';
  const geo = getCountryGeography(country);
  const regionChoices = geo.regions || [];
  const localityChoices = localitiesForRegion(country, formData?.region);
  const allLocalities =
    localityChoices.length > 0 ? localityChoices : Object.values(geo.localities || {}).flat();
  const industryOptions = getActiveIndustryOptions(undefined, businessUnit);
  const subindustryOptions = getActiveSubindustryOptions(formData?.industry, businessUnit);
  const regionFreeText = Boolean(geo.freeText) || regionChoices.length === 0;
  const localityFreeText = regionFreeText || allLocalities.length === 0;

  const regionSelectOptions = [
    { value: '', label: `Seleccionar ${String(geo.regionLabel || 'región').toLowerCase()}` },
    ...regionChoices,
    ...(formData?.region && !regionChoices.some((option) => option.value === formData.region)
      ? [{ value: formData.region, label: formData.region }]
      : []),
  ];
  const localitySelectOptions = [
    { value: '', label: `Seleccionar ${String(geo.localityLabel || 'comuna').toLowerCase()}` },
    ...allLocalities,
    ...(formData?.commune && !allLocalities.some((option) => option.value === formData.commune)
      ? [{ value: formData.commune, label: formData.commune }]
      : []),
  ];

  const findRegionForLocality = (localityValue, geography) => {
    const needle = String(localityValue || '').trim().toLowerCase();
    if (!needle) return '';
    const entries = Object.entries(geography?.localities || {});
    for (const [regionName, list] of entries) {
      if ((list || []).some((item) => String(item.value || '').trim().toLowerCase() === needle)) {
        return regionName;
      }
    }
    return '';
  };

  const handleTopLevelRegionChange = (value) => {
    onChange('region', value);
    const allowed = localitiesForRegion(country, value).map((option) => option.value);
    if (formData?.commune && allowed.length && !allowed.includes(formData.commune)) {
      onChange('commune', '');
    }
  };
  const handleTopLevelCommuneChange = (value) => {
    onChange('commune', value);
    const mappedRegion = findRegionForLocality(value, geo);
    if (mappedRegion && mappedRegion !== formData?.region) {
      onChange('region', mappedRegion);
    }
  };
  const handleCountryChange = (nextCountry) => {
    const next = canonicalCountry(nextCountry) || nextCountry;
    const prevMeta = phoneMetaForCountry(country);
    const nextMeta = phoneMetaForCountry(next);
    const nextGeo = getCountryGeography(next);
    onChange('country', next);
    const regionOk = (nextGeo.regions || []).some((option) => option.value === formData?.region);
    if (formData?.region && !regionOk && (nextGeo.regions || []).length) {
      onChange('region', '');
    }
    const nextLocalities = localitiesForRegion(next, regionOk ? formData?.region : '');
    if (formData?.commune && nextLocalities.length && !nextLocalities.some((option) => option.value === formData.commune)) {
      onChange('commune', '');
    }
    if (String(formData?.phone || '').trim() && prevMeta.callingCode !== nextMeta.callingCode) {
      onChange('phone', nextMeta.callingCode ? `+${nextMeta.callingCode}` : '');
    }
    setPhoneError('');
  };
  const handleDrawerChange = (key, value) => {
    if (key === 'country') {
      handleCountryChange(value);
      return;
    }
    if (key === 'phone') {
      onChange('phone', formatPhoneForCountry(value, country));
      if (phoneError) setPhoneError('');
      return;
    }
    onChange(key, value);
  };
  const handleSave = () => {
    const error = phoneErrorForCountry(formData?.phone, country);
    if (error) {
      setPhoneError(error);
      return;
    }
    setPhoneError('');
    onSave?.();
  };

  const normalize = (value = '') =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const inputNameNormalized = normalize(formData?.org_name || '');
  const similarOrganizations = inputNameNormalized
    ? existingOrganizations.filter((organization) => {
        if (Number(organization?.id) === Number(currentOrganizationId)) return false;
        const candidateName = normalize(organization?.org_name || '');
        if (!candidateName) return false;
        return (
          candidateName === inputNameNormalized ||
          candidateName.includes(inputNameNormalized) ||
          inputNameNormalized.includes(candidateName)
        );
      })
    : [];
  const duplicateWarningText = similarOrganizations.length
    ? `Coincidencias encontradas: ${similarOrganizations
        .slice(0, 3)
        .map((organization) => organization.org_name)
        .join(', ')}`
    : '';

  const contacts = Array.isArray(formData.contacts) ? formData.contacts : [];
  const updateContact = (index, key, value) => {
    const nextContacts = contacts.map((contact, idx) =>
      idx === index ? { ...contact, [key]: value } : contact
    );
    onChange('contacts', nextContacts);
  };
  const addContact = () => {
    onChange('contacts', [
      ...contacts,
      { id: null, full_name: '', role: '', email: '', phone: '', linkedin: '', status: 'Activo' },
    ]);
  };
  const removeContact = (index) => {
    onChange(
      'contacts',
      contacts.filter((_, idx) => idx !== index)
    );
  };

  const units = Array.isArray(formData.units) ? formData.units : [];
  const updateUnit = (index, key, value) => {
    const nextUnits = units.map((unit, idx) => (idx === index ? { ...unit, [key]: value } : unit));
    onChange('units', nextUnits);
  };
  const updateUnitRegion = (index, regionValue) => {
    const nextUnits = units.map((row, idx) => {
      if (idx !== index) return row;
      const allowed = localitiesForRegion(row.country || country, regionValue).map((option) => option.value);
      return {
        ...row,
        region: regionValue,
        commune: row.commune && allowed.length && !allowed.includes(row.commune) ? '' : row.commune,
      };
    });
    onChange('units', nextUnits);
  };
  const updateUnitCommune = (index, communeValue) => {
    const nextUnits = units.map((row, idx) => {
      if (idx !== index) return row;
      const mappedRegion = findRegionForLocality(communeValue, getCountryGeography(row.country || country));
      return { ...row, commune: communeValue, region: mappedRegion || row.region || '' };
    });
    onChange('units', nextUnits);
  };
  const updateUnitCountry = (index, nextCountry) => {
    const next = canonicalCountry(nextCountry) || nextCountry;
    const nextUnits = units.map((row, idx) => {
      if (idx !== index) return row;
      const nextGeo = getCountryGeography(next);
      const regionOk = (nextGeo.regions || []).some((option) => option.value === row.region);
      return {
        ...row,
        country: next,
        region: regionOk ? row.region : '',
        commune: regionOk ? row.commune : '',
      };
    });
    onChange('units', nextUnits);
  };
  const addUnit = () => {
    onChange('units', [
      ...units,
      {
        id: null,
        unit_name: '',
        unit_code: '',
        address: '',
        commune: '',
        region: '',
        country: country || 'Chile',
        notes: '',
      },
    ]);
  };
  const removeUnit = (index) => {
    onChange(
      'units',
      units.filter((_, idx) => idx !== index)
    );
  };

  const sections = [
    {
      key: 'basic',
      title: 'Datos básicos',
      fields: [
        { key: 'org_code', label: 'Código', disabled: true, readOnly: true },
        {
          key: 'org_name',
          label: 'Nombre',
          type: 'custom',
          render: ({ value, onChange: onOrgNameChange, colors, baseInputStyle }) => (
            <div>
              <label style={{ fontSize: '12px', color: colors.text, display: 'block' }}>
                Nombre
              </label>
              <input
                style={{ ...baseInputStyle, marginTop: '6px' }}
                value={value || ''}
                onChange={(event) => onOrgNameChange(event.target.value)}
                onBlur={() => {
                  const current = String(formData?.org_name || '');
                  const normalized = normalizeProperCaseText(current);
                  if (normalized !== current) onChange('org_name', normalized);
                }}
                placeholder="Nombre de la organización"
              />
              {duplicateWarningText ? (
                <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#dc2626' }}>{duplicateWarningText}</p>
              ) : null}
            </div>
          ),
        },
        { key: 'rut', label: 'RUT' },
        {
          key: 'segment',
          label: 'Segmento',
          type: 'select',
          options: [
            { value: '', label: 'Seleccionar segmento' },
            ...ORGANIZATION_CLASSIFICATION_OPTIONS,
          ],
        },
        {
          key: 'channel',
          label: 'Canal',
          type: 'custom',
          render: ({ value, colors }) => {
            const selected = parseOrganizationChannels(value);
            const selectedSet = new Set(selected);
            const catalogOptions = getActiveChannelOptions(undefined, businessUnit);
            const knownValues = new Set(catalogOptions.map((option) => option.value));
            const extraOptions = selected
              .filter((item) => !knownValues.has(item))
              .map((item) => ({ value: item, label: item }));
            const options = [...catalogOptions, ...extraOptions];

            const toggleChannel = (channelValue) => {
              const next = new Set(selectedSet);
              if (next.has(channelValue)) next.delete(channelValue);
              else next.add(channelValue);
              onChange('channel', serializeOrganizationChannels([...next]));
            };

            return (
              <div>
                <span style={{ fontSize: '12px', color: colors.text, display: 'block' }}>Canal</span>
                <div
                  style={{
                    marginTop: '6px',
                    border: `1px solid ${colors.inputBorder}`,
                    borderRadius: '8px',
                    background: colors.inputBg,
                    padding: '8px 10px',
                    display: 'grid',
                    gap: '6px',
                    maxHeight: '160px',
                    overflowY: 'auto',
                  }}
                >
                  {options.length ? (
                    options.map((option) => {
                      const checked = selectedSet.has(option.value);
                      return (
                        <label
                          key={option.value}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '12px',
                            color: colors.inputText || colors.text,
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleChannel(option.value)}
                            style={{ margin: 0 }}
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })
                  ) : (
                    <span style={{ fontSize: '12px', color: colors.muted }}>
                      No hay canales configurados en el catálogo.
                    </span>
                  )}
                </div>
                <span style={{ display: 'block', marginTop: '4px', fontSize: '11px', color: colors.muted }}>
                  Selección múltiple
                  {selected.length
                    ? ` · ${selected.length} seleccionado${selected.length === 1 ? '' : 's'}`
                    : ''}
                </span>
              </div>
            );
          },
        },
        {
          key: 'assigned_user_id',
          label: 'Responsable',
          type: 'select',
          options: executiveOptions,
        },
        {
          key: 'industry',
          label: 'Industria',
          type: 'custom',
          render: ({ value, colors, baseInputStyle }) => (
            <label style={{ fontSize: '12px', color: colors.text, display: 'flex', flexDirection: 'column' }}>
              Industria
              <select
                style={{ ...baseInputStyle, marginTop: '6px' }}
                value={value || ''}
                onChange={(event) => {
                  const nextIndustry = event.target.value;
                  onChange('industry', nextIndustry);
                  const allowedSubindustries = getActiveSubindustryOptions(nextIndustry, businessUnit).map(
                    (option) => option.value
                  );
                  if (formData?.subindustry && !allowedSubindustries.includes(formData.subindustry)) {
                    onChange('subindustry', '');
                  }
                }}
              >
                <option value="">Seleccionar industria</option>
                {industryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                {formData?.industry && !industryOptions.some((option) => option.value === formData.industry) ? (
                  <option value={formData.industry}>{formData.industry}</option>
                ) : null}
              </select>
            </label>
          ),
        },
        {
          key: 'subindustry',
          label: 'Subindustria',
          type: 'custom',
          render: ({ value, colors, baseInputStyle }) => (
            <label style={{ fontSize: '12px', color: colors.text, display: 'flex', flexDirection: 'column' }}>
              Subindustria
              <select
                style={{ ...baseInputStyle, marginTop: '6px' }}
                value={value || ''}
                onChange={(event) => onChange('subindustry', event.target.value)}
                disabled={!formData?.industry}
              >
                <option value="">
                  {formData?.industry ? 'Seleccionar subindustria' : 'Selecciona una industria primero'}
                </option>
                {subindustryOptions.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
                {formData?.subindustry &&
                !subindustryOptions.some((option) => option.value === formData.subindustry) ? (
                  <option value={formData.subindustry}>{formData.subindustry}</option>
                ) : null}
              </select>
            </label>
          ),
        },
      ],
    },
    {
      key: 'additional',
      title: 'Datos adicionales',
      fields: [
        {
          key: 'country',
          label: 'País',
          type: 'select',
          options: [
            { value: '', label: 'Seleccionar país' },
            ...ORGANIZATION_COUNTRY_OPTIONS,
            ...(formData?.country &&
            !ORGANIZATION_COUNTRY_OPTIONS.some((option) => option.value === formData.country)
              ? [{ value: formData.country, label: formData.country }]
              : []),
          ],
        },
        {
          key: 'region',
          label: geo.regionLabel || 'Región',
          type: 'custom',
          render: ({ value, colors, baseInputStyle }) => (
            <label style={{ fontSize: '12px', color: colors.text, display: 'flex', flexDirection: 'column' }}>
              {geo.regionLabel || 'Región'}
              {regionFreeText ? (
                <input
                  style={{ ...baseInputStyle, marginTop: '6px' }}
                  value={value || ''}
                  placeholder={geo.regionLabel || 'Región'}
                  onChange={(event) => onChange('region', event.target.value)}
                />
              ) : (
                <select
                  style={{ ...baseInputStyle, marginTop: '6px' }}
                  value={value || ''}
                  onChange={(event) => handleTopLevelRegionChange(event.target.value)}
                >
                  {regionSelectOptions.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </label>
          ),
        },
        {
          key: 'commune',
          label: geo.localityLabel || 'Comuna',
          type: 'custom',
          render: ({ value, colors, baseInputStyle }) => (
            <label style={{ fontSize: '12px', color: colors.text, display: 'flex', flexDirection: 'column' }}>
              {geo.localityLabel || 'Comuna'}
              {localityFreeText ? (
                <input
                  style={{ ...baseInputStyle, marginTop: '6px' }}
                  value={value || ''}
                  placeholder={geo.localityLabel || 'Comuna'}
                  onChange={(event) => onChange('commune', event.target.value)}
                />
              ) : (
                <select
                  style={{ ...baseInputStyle, marginTop: '6px' }}
                  value={value || ''}
                  onChange={(event) => handleTopLevelCommuneChange(event.target.value)}
                >
                  {localitySelectOptions.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </label>
          ),
        },
        {
          key: 'address',
          label: 'Dirección',
          fullWidth: true,
          type: 'custom',
          render: ({ value, onChange: onAddressChange, colors, baseInputStyle }) => (
            <label style={{ fontSize: '12px', color: colors.text, display: 'flex', flexDirection: 'column' }}>
              Dirección
              <input
                style={{ ...baseInputStyle, marginTop: '6px' }}
                value={value || ''}
                onChange={(event) => onAddressChange(event.target.value)}
                onBlur={() => {
                  const current = String(formData?.address || '');
                  const normalized = normalizeProperCaseText(current);
                  if (normalized !== current) onChange('address', normalized);
                }}
              />
            </label>
          ),
        },
        {
          key: 'phone',
          label: 'Teléfono',
          type: 'custom',
          render: ({ value, colors, baseInputStyle }) => (
            <label style={{ fontSize: '12px', color: colors.text, display: 'flex', flexDirection: 'column' }}>
              Teléfono
              <input
                style={{ ...baseInputStyle, marginTop: '6px' }}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                maxLength={PHONE_MAX_LENGTH}
                placeholder={phonePlaceholderForCountry(country)}
                value={formatPhoneForCountry(value, country)}
                onChange={(event) => handleDrawerChange('phone', event.target.value)}
              />
            </label>
          ),
        },
        { key: 'email', label: 'Correo', type: 'email' },
        { key: 'website', label: 'Web' },
        { key: 'linkedin', label: 'LinkedIn' },
      ],
    },
    {
      key: 'units',
      title: 'Subunidades',
      fields: [
        {
          key: 'units',
          fullWidth: true,
          type: 'custom',
          render: ({ value, colors, baseInputStyle }) => {
            const localUnits = Array.isArray(value) ? value : [];
            return (
              <div style={{ display: 'grid', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: colors.muted }}>
                    Sucursales, plantas u oficinas bajo la misma razón social.
                  </span>
                  <button
                    type="button"
                    onClick={addUnit}
                    style={{
                      height: '30px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.inputBorder}`,
                      background: 'transparent',
                      color: colors.text,
                      padding: '0 10px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    + Agregar subunidad
                  </button>
                </div>

                {!localUnits.length ? (
                  <div style={{ fontSize: '12px', color: colors.muted, padding: '8px 0' }}>
                    Sin subunidades agregadas.
                  </div>
                ) : null}

                {localUnits.map((unit, index) => (
                  <div
                    key={`unit-row-${unit.id ?? `new-${index}`}`}
                    style={{
                      border: `1px solid ${colors.inputBorder}`,
                      borderRadius: '8px',
                      padding: '10px',
                      display: 'grid',
                      gap: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: '12px', color: colors.text }}>Subunidad {index + 1}</strong>
                      <button
                        type="button"
                        onClick={() => removeUnit(index)}
                        style={{
                          height: '28px',
                          borderRadius: '8px',
                          border: `1px solid ${colors.inputBorder}`,
                          background: 'transparent',
                          color: colors.text,
                          padding: '0 8px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Quitar
                      </button>
                    </div>
                    <input
                      style={baseInputStyle}
                      placeholder="Nombre de la subunidad *"
                      value={unit.unit_name || ''}
                      onChange={(event) => updateUnit(index, 'unit_name', event.target.value)}
                      onBlur={() => {
                        const current = String(unit.unit_name || '');
                        const normalized = normalizeProperCaseText(current);
                        if (normalized !== current) updateUnit(index, 'unit_name', normalized);
                      }}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                      <input
                        style={baseInputStyle}
                        placeholder="Código interno (opcional)"
                        value={unit.unit_code || ''}
                        onChange={(event) => updateUnit(index, 'unit_code', event.target.value)}
                      />
                      <select
                        style={baseInputStyle}
                        value={canonicalCountry(unit.country) || unit.country || ''}
                        onChange={(event) => updateUnitCountry(index, event.target.value)}
                      >
                        <option value="">Seleccionar país</option>
                        {ORGANIZATION_COUNTRY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <input
                        style={{ ...baseInputStyle, gridColumn: '1 / -1' }}
                        placeholder="Dirección"
                        value={unit.address || ''}
                        onChange={(event) => updateUnit(index, 'address', event.target.value)}
                        onBlur={() => {
                          const current = String(unit.address || '');
                          const normalized = normalizeProperCaseText(current);
                          if (normalized !== current) updateUnit(index, 'address', normalized);
                        }}
                      />
                      {(() => {
                        const unitCountry = canonicalCountry(unit.country) || country;
                        const unitGeo = getCountryGeography(unitCountry);
                        const unitRegions = unitGeo.regions || [];
                        const unitLocalities = localitiesForRegion(unitCountry, unit.region);
                        const unitRegionFree = Boolean(unitGeo.freeText) || unitRegions.length === 0;
                        const unitLocalityFree = unitRegionFree || unitLocalities.length === 0;
                        return (
                          <>
                            {unitRegionFree ? (
                              <input
                                style={baseInputStyle}
                                placeholder={unitGeo.regionLabel || 'Región'}
                                value={unit.region || ''}
                                onChange={(event) => updateUnit(index, 'region', event.target.value)}
                              />
                            ) : (
                              <select
                                style={baseInputStyle}
                                value={unit.region || ''}
                                onChange={(event) => updateUnitRegion(index, event.target.value)}
                              >
                                <option value="">{`Seleccionar ${String(unitGeo.regionLabel || 'región').toLowerCase()}`}</option>
                                {unitRegions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            )}
                            {unitLocalityFree ? (
                              <input
                                style={baseInputStyle}
                                placeholder={unitGeo.localityLabel || 'Comuna'}
                                value={unit.commune || ''}
                                onChange={(event) => updateUnit(index, 'commune', event.target.value)}
                              />
                            ) : (
                              <select
                                style={baseInputStyle}
                                value={unit.commune || ''}
                                onChange={(event) => updateUnitCommune(index, event.target.value)}
                              >
                                <option value="">{`Seleccionar ${String(unitGeo.localityLabel || 'comuna').toLowerCase()}`}</option>
                                {unitLocalities.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            )}
                          </>
                        );
                      })()}
                      <input
                        style={{ ...baseInputStyle, gridColumn: '1 / -1' }}
                        placeholder="Notas"
                        value={unit.notes || ''}
                        onChange={(event) => updateUnit(index, 'notes', event.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            );
          },
        },
      ],
    },
    {
      key: 'contacts',
      title: 'Contactos asociados',
      fields: [
        {
          key: 'contacts',
          fullWidth: true,
          type: 'custom',
          render: ({ value, colors, baseInputStyle }) => {
            const localContacts = Array.isArray(value) ? value : [];
            return (
              <div style={{ display: 'grid', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: colors.muted }}>
                    Agrega uno o más contactos en el mismo formulario.
                  </span>
                  <button
                    type="button"
                    onClick={addContact}
                    style={{
                      height: '30px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.inputBorder}`,
                      background: 'transparent',
                      color: colors.text,
                      padding: '0 10px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    + Agregar contacto
                  </button>
                </div>

                {!localContacts.length ? (
                  <div style={{ fontSize: '12px', color: colors.muted, padding: '8px 0' }}>
                    Sin contactos agregados.
                  </div>
                ) : null}

                {localContacts.map((contact, index) => (
                  <div
                    key={`contact-row-${index}`}
                    style={{
                      border: `1px solid ${colors.inputBorder}`,
                      borderRadius: '8px',
                      padding: '10px',
                      display: 'grid',
                      gap: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: '12px', color: colors.text }}>Contacto {index + 1}</strong>
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        style={{
                          height: '28px',
                          borderRadius: '8px',
                          border: `1px solid ${colors.inputBorder}`,
                          background: 'transparent',
                          color: colors.text,
                          padding: '0 8px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Quitar
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                      <input
                        style={baseInputStyle}
                        placeholder="Nombre completo"
                        value={contact.full_name || ''}
                        onChange={(event) => updateContact(index, 'full_name', event.target.value)}
                        onBlur={() => {
                          const current = String(contact.full_name || '');
                          const normalized = normalizeProperCaseText(current);
                          if (normalized !== current) updateContact(index, 'full_name', normalized);
                        }}
                      />
                      <input
                        style={baseInputStyle}
                        placeholder="Cargo"
                        value={contact.role || ''}
                        onChange={(event) => updateContact(index, 'role', event.target.value)}
                        onBlur={() => {
                          const current = String(contact.role || '');
                          const normalized = normalizeProperCaseText(current);
                          if (normalized !== current) updateContact(index, 'role', normalized);
                        }}
                      />
                      <input
                        style={baseInputStyle}
                        placeholder="Correo"
                        value={contact.email || ''}
                        onChange={(event) => updateContact(index, 'email', event.target.value)}
                      />
                      <input
                        style={baseInputStyle}
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        maxLength={PHONE_MAX_LENGTH}
                        placeholder={phonePlaceholderForCountry(country)}
                        value={formatPhoneForCountry(contact.phone || '', country)}
                        onChange={(event) =>
                          updateContact(index, 'phone', formatPhoneForCountry(event.target.value, country))
                        }
                      />
                      <input
                        style={{ ...baseInputStyle, gridColumn: '1 / -1' }}
                        placeholder="LinkedIn"
                        value={contact.linkedin || ''}
                        onChange={(event) => updateContact(index, 'linkedin', event.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            );
          },
        },
      ],
    },
  ];

  return (
    <FormDrawer
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      isSaving={isSaving}
      isDarkMode={isDarkMode}
      title={isEditing ? 'Editar Organización' : 'Agregar Organización'}
      subtitle="Formulario minimalista"
      sections={sections}
      formData={formData}
      onChange={handleDrawerChange}
      formError={phoneError || formError}
      width={560}
    />
  );
};

export default OrganizationsForm;
